/**
 * Barracks (兵营) - Swordsman training building
 * Features: Dark blue-gray tiled roof, open front with wooden railings, hanging lantern, red flag
 * Design: Front is open (veranda style) so interior is visible from isometric view
 * Size: 3x3 tiles
 */

import * as THREE from 'three';

export function createBarracksMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  // Materials
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x808080, roughness: 0.9 });
  const stoneEdgeMat = new THREE.MeshStandardMaterial({ color: 0x606060, roughness: 0.85 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3a2518, roughness: 0.85 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5a3a25, roughness: 0.8 });
  const lightWoodMat = new THREE.MeshStandardMaterial({ color: 0x6a4a35, roughness: 0.75 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x384858, roughness: 0.6 });
  const roofTileMat = new THREE.MeshStandardMaterial({ color: 0x455565, roughness: 0.65 });
  const roofEdgeMat = new THREE.MeshStandardMaterial({ color: 0x283848, roughness: 0.55 });
  const lanternGlowMat = new THREE.MeshStandardMaterial({
    color: 0xffdd99,
    emissive: 0xffaa44,
    emissiveIntensity: 0.8
  });
  const lanternFrameMat = new THREE.MeshStandardMaterial({ color: 0x2a1a10, roughness: 0.8 });
  const flagMat = new THREE.MeshStandardMaterial({
    color: 0xcc3333,
    side: THREE.DoubleSide,
    roughness: 0.85
  });
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xd4aa44, metalness: 0.6, roughness: 0.35 });

  // ============================================
  // STONE PLATFORM BASE
  // ============================================

  // Main platform - irregular octagon shape
  const platformGeom = new THREE.CylinderGeometry(1.5, 1.6, 0.3, 8);
  const platform = new THREE.Mesh(platformGeom, stoneMat);
  platform.position.y = 0.15;
  platform.castShadow = true;
  platform.receiveShadow = true;
  group.add(platform);

  // Platform top edge
  const platformEdgeGeom = new THREE.CylinderGeometry(1.55, 1.55, 0.08, 8);
  const platformEdge = new THREE.Mesh(platformEdgeGeom, stoneEdgeMat);
  platformEdge.position.y = 0.34;
  group.add(platformEdge);

  // Front steps
  const stepGeom1 = new THREE.BoxGeometry(0.9, 0.12, 0.3);
  const step1 = new THREE.Mesh(stepGeom1, stoneMat);
  step1.position.set(0, 0.06, 1.55);
  step1.castShadow = true;
  group.add(step1);

  const stepGeom2 = new THREE.BoxGeometry(0.8, 0.12, 0.28);
  const step2 = new THREE.Mesh(stepGeom2, stoneMat);
  step2.position.set(0, 0.18, 1.75);
  step2.castShadow = true;
  group.add(step2);

  const stepGeom3 = new THREE.BoxGeometry(0.7, 0.12, 0.26);
  const step3 = new THREE.Mesh(stepGeom3, stoneMat);
  step3.position.set(0, 0.3, 1.92);
  step3.castShadow = true;
  group.add(step3);

  // ============================================
  // WOODEN FLOOR
  // ============================================
  const floorGeom = new THREE.BoxGeometry(2.4, 0.06, 2.0);
  const floor = new THREE.Mesh(floorGeom, woodMat);
  floor.position.set(0, 0.41, -0.1);
  floor.receiveShadow = true;
  group.add(floor);

  // ============================================
  // BACK AND SIDE WALLS (enclosed back area)
  // ============================================

  // Back wall
  const backWallGeom = new THREE.BoxGeometry(2.2, 1.2, 0.12);
  const backWall = new THREE.Mesh(backWallGeom, darkWoodMat);
  backWall.position.set(0, 1.0, -1.0);
  backWall.castShadow = true;
  group.add(backWall);

  // Left wall
  const sideWallGeom = new THREE.BoxGeometry(0.12, 1.2, 1.2);
  const leftWall = new THREE.Mesh(sideWallGeom, darkWoodMat);
  leftWall.position.set(-1.05, 1.0, -0.45);
  leftWall.castShadow = true;
  group.add(leftWall);

  // Right wall
  const rightWall = new THREE.Mesh(sideWallGeom.clone(), darkWoodMat);
  rightWall.position.set(1.05, 1.0, -0.45);
  rightWall.castShadow = true;
  group.add(rightWall);

  // ============================================
  // FRONT WOODEN RAILING (Open veranda)
  // ============================================

  // Corner posts
  const postGeom = new THREE.BoxGeometry(0.1, 1.3, 0.1);

  const frontLeftPost = new THREE.Mesh(postGeom, woodMat);
  frontLeftPost.position.set(-1.0, 1.05, 0.7);
  frontLeftPost.castShadow = true;
  group.add(frontLeftPost);

  const frontRightPost = new THREE.Mesh(postGeom, woodMat);
  frontRightPost.position.set(1.0, 1.05, 0.7);
  frontRightPost.castShadow = true;
  group.add(frontRightPost);

  // Door frame posts
  const doorPostGeom = new THREE.BoxGeometry(0.1, 1.3, 0.1);
  const doorLeftPost = new THREE.Mesh(doorPostGeom, woodMat);
  doorLeftPost.position.set(-0.4, 1.05, 0.7);
  doorLeftPost.castShadow = true;
  group.add(doorLeftPost);

  const doorRightPost = new THREE.Mesh(doorPostGeom, woodMat);
  doorRightPost.position.set(0.4, 1.05, 0.7);
  doorRightPost.castShadow = true;
  group.add(doorRightPost);

  // Railing panels - LEFT SIDE
  // Top rail
  const leftTopRailGeom = new THREE.BoxGeometry(0.55, 0.06, 0.06);
  const leftTopRail = new THREE.Mesh(leftTopRailGeom, lightWoodMat);
  leftTopRail.position.set(-0.7, 1.1, 0.7);
  group.add(leftTopRail);

  // Middle rail
  const leftMidRail = new THREE.Mesh(leftTopRailGeom.clone(), lightWoodMat);
  leftMidRail.position.set(-0.7, 0.8, 0.7);
  group.add(leftMidRail);

  // Bottom rail
  const leftBotRail = new THREE.Mesh(leftTopRailGeom.clone(), lightWoodMat);
  leftBotRail.position.set(-0.7, 0.55, 0.7);
  group.add(leftBotRail);

  // Vertical slats - left
  const slatGeom = new THREE.BoxGeometry(0.035, 0.6, 0.035);
  for (let i = 0; i < 5; i++) {
    const slat = new THREE.Mesh(slatGeom, lightWoodMat);
    slat.position.set(-0.92 + i * 0.12, 0.82, 0.7);
    group.add(slat);
  }

  // Railing panels - RIGHT SIDE
  const rightTopRail = new THREE.Mesh(leftTopRailGeom.clone(), lightWoodMat);
  rightTopRail.position.set(0.7, 1.1, 0.7);
  group.add(rightTopRail);

  const rightMidRail = new THREE.Mesh(leftTopRailGeom.clone(), lightWoodMat);
  rightMidRail.position.set(0.7, 0.8, 0.7);
  group.add(rightMidRail);

  const rightBotRail = new THREE.Mesh(leftTopRailGeom.clone(), lightWoodMat);
  rightBotRail.position.set(0.7, 0.55, 0.7);
  group.add(rightBotRail);

  // Vertical slats - right
  for (let i = 0; i < 5; i++) {
    const slat = new THREE.Mesh(slatGeom, lightWoodMat);
    slat.position.set(0.48 + i * 0.12, 0.82, 0.7);
    group.add(slat);
  }

  // Door top beam
  const doorTopBeamGeom = new THREE.BoxGeometry(0.9, 0.1, 0.1);
  const doorTopBeam = new THREE.Mesh(doorTopBeamGeom, woodMat);
  doorTopBeam.position.set(0, 1.72, 0.7);
  group.add(doorTopBeam);

  // ============================================
  // ROOF STRUCTURE - Traditional Chinese style
  // ============================================

  // Main roof eave (底层屋檐)
  const eaveGeom = new THREE.BoxGeometry(2.8, 0.1, 2.5);
  const eave = new THREE.Mesh(eaveGeom, roofEdgeMat);
  eave.position.y = 1.75;
  eave.castShadow = true;
  group.add(eave);

  // Roof tiles layer 1
  const roofLayer1Geom = new THREE.BoxGeometry(2.5, 0.12, 2.2);
  const roofLayer1 = new THREE.Mesh(roofLayer1Geom, roofMat);
  roofLayer1.position.y = 1.88;
  roofLayer1.castShadow = true;
  group.add(roofLayer1);

  // Roof tiles layer 2
  const roofLayer2Geom = new THREE.BoxGeometry(2.1, 0.12, 1.85);
  const roofLayer2 = new THREE.Mesh(roofLayer2Geom, roofTileMat);
  roofLayer2.position.y = 2.0;
  roofLayer2.castShadow = true;
  group.add(roofLayer2);

  // Roof tiles layer 3
  const roofLayer3Geom = new THREE.BoxGeometry(1.6, 0.12, 1.4);
  const roofLayer3 = new THREE.Mesh(roofLayer3Geom, roofMat);
  roofLayer3.position.y = 2.12;
  roofLayer3.castShadow = true;
  group.add(roofLayer3);

  // Roof peak
  const roofPeakGeom = new THREE.ConeGeometry(0.9, 0.4, 4);
  const roofPeak = new THREE.Mesh(roofPeakGeom, roofMat);
  roofPeak.position.y = 2.42;
  roofPeak.rotation.y = Math.PI / 4;
  roofPeak.castShadow = true;
  group.add(roofPeak);

  // Roof top ornament
  const roofOrnamentGeom = new THREE.SphereGeometry(0.08, 8, 6);
  const roofOrnament = new THREE.Mesh(roofOrnamentGeom, roofEdgeMat);
  roofOrnament.position.y = 2.68;
  group.add(roofOrnament);

  // ============================================
  // UPTURNED EAVE CORNERS (飞檐翘角) - 连接到屋檐
  // ============================================
  const createEaveCorner = (x: number, z: number, rotY: number) => {
    // 翘起的檐角 - 从屋檐边缘延伸出去
    const cornerGeom = new THREE.BoxGeometry(0.4, 0.1, 0.12);
    const corner = new THREE.Mesh(cornerGeom, roofEdgeMat);
    corner.position.set(x * 0.95, 1.78, z * 0.92);
    corner.rotation.y = rotY;
    corner.rotation.z = -0.3;
    group.add(corner);
  };

  // 四个角 - 位置紧贴屋檐边缘
  createEaveCorner(1.35, 1.2, -Math.PI / 4);
  createEaveCorner(-1.35, 1.2, Math.PI / 4 + Math.PI / 2);
  createEaveCorner(1.35, -1.2, Math.PI / 4);
  createEaveCorner(-1.35, -1.2, -Math.PI / 4 - Math.PI / 2);

  // ============================================
  // HANGING LANTERN (Interior)
  // ============================================

  // Lantern chain
  const chainGeom = new THREE.CylinderGeometry(0.015, 0.015, 0.4, 6);
  const chain = new THREE.Mesh(chainGeom, new THREE.MeshStandardMaterial({ color: 0x333333, metalness: 0.7 }));
  chain.position.set(0, 1.5, 0.15);
  group.add(chain);

  // Lantern top cap
  const lanternTopGeom = new THREE.CylinderGeometry(0.06, 0.1, 0.06, 8);
  const lanternTop = new THREE.Mesh(lanternTopGeom, lanternFrameMat);
  lanternTop.position.set(0, 1.35, 0.15);
  group.add(lanternTop);

  // Lantern body (glowing)
  const lanternBodyGeom = new THREE.CylinderGeometry(0.12, 0.14, 0.3, 8);
  const lanternBody = new THREE.Mesh(lanternBodyGeom, lanternGlowMat);
  lanternBody.position.set(0, 1.15, 0.15);
  group.add(lanternBody);

  // Lantern bottom cap
  const lanternBotGeom = new THREE.CylinderGeometry(0.1, 0.06, 0.05, 8);
  const lanternBot = new THREE.Mesh(lanternBotGeom, lanternFrameMat);
  lanternBot.position.set(0, 0.97, 0.15);
  group.add(lanternBot);

  // Lantern tassel
  const tasselGeom = new THREE.ConeGeometry(0.04, 0.12, 6);
  const tasselMat = new THREE.MeshStandardMaterial({ color: 0xcc3333, roughness: 0.9 });
  const tassel = new THREE.Mesh(tasselGeom, tasselMat);
  tassel.position.set(0, 0.88, 0.15);
  tassel.rotation.x = Math.PI;
  group.add(tassel);

  // Floor glow from lantern
  const floorGlowGeom = new THREE.CircleGeometry(0.6, 16);
  const floorGlowMat = new THREE.MeshStandardMaterial({
    color: 0xffcc88,
    emissive: 0xff9944,
    emissiveIntensity: 0.4,
    transparent: true,
    opacity: 0.5
  });
  const floorGlow = new THREE.Mesh(floorGlowGeom, floorGlowMat);
  floorGlow.position.set(0, 0.45, 0.15);
  floorGlow.rotation.x = -Math.PI / 2;
  group.add(floorGlow);

  // ============================================
  // RED FLAG (Right side) - 旗杆在建筑外面
  // ============================================

  const flagPoleX = 1.75;  // 更靠右
  const flagPoleZ = 0.9;   // 更靠前，在建筑外面

  // Flag pole
  const poleGeom = new THREE.CylinderGeometry(0.03, 0.04, 2.0, 6);
  const pole = new THREE.Mesh(poleGeom, woodMat);
  pole.position.set(flagPoleX, 1.35, flagPoleZ);
  pole.castShadow = true;
  group.add(pole);

  // Pole base
  const poleBaseGeom = new THREE.CylinderGeometry(0.08, 0.1, 0.2, 8);
  const poleBase = new THREE.Mesh(poleBaseGeom, stoneMat);
  poleBase.position.set(flagPoleX, 0.45, flagPoleZ);
  group.add(poleBase);

  // Pole top ball
  const poleTopGeom = new THREE.SphereGeometry(0.06, 8, 6);
  const poleTop = new THREE.Mesh(poleTopGeom, goldMat);
  poleTop.position.set(flagPoleX, 2.38, flagPoleZ);
  group.add(poleTop);

  // Flag - 倾斜悬挂，从俯视图可见
  const flagGeom = new THREE.PlaneGeometry(0.6, 0.8);
  // Add wave effect
  const flagPos = flagGeom.attributes.position;
  for (let i = 0; i < flagPos.count; i++) {
    const x = flagPos.getX(i);
    flagPos.setZ(i, Math.sin(x * 3) * 0.05);
  }
  flagPos.needsUpdate = true;
  flagGeom.computeVertexNormals();

  const flag = new THREE.Mesh(flagGeom, flagMat);
  flag.position.set(flagPoleX, 1.85, flagPoleZ + 0.4);  // 旗帜向前飘
  flag.rotation.x = -0.6;  // 向前倾斜
  group.add(flag);

  // 旗帜顶部连接横杆
  const flagBarGeom = new THREE.CylinderGeometry(0.015, 0.015, 0.45, 4);
  const flagBar = new THREE.Mesh(flagBarGeom, woodMat);
  flagBar.position.set(flagPoleX, 2.25, flagPoleZ + 0.2);
  flagBar.rotation.x = Math.PI / 2;
  group.add(flagBar);

  // ============================================
  // INTERIOR DETAILS
  // ============================================

  // Weapon rack on back wall
  const rackGeom = new THREE.BoxGeometry(0.6, 0.08, 0.08);
  const rack1 = new THREE.Mesh(rackGeom, woodMat);
  rack1.position.set(-0.5, 1.1, -0.9);
  group.add(rack1);
  const rack2 = new THREE.Mesh(rackGeom.clone(), woodMat);
  rack2.position.set(-0.5, 0.75, -0.9);
  group.add(rack2);

  // Swords on rack
  const swordMat = new THREE.MeshStandardMaterial({ color: 0xaaaaaa, metalness: 0.8, roughness: 0.3 });
  for (let i = 0; i < 3; i++) {
    const bladeGeom = new THREE.BoxGeometry(0.02, 0.45, 0.01);
    const blade = new THREE.Mesh(bladeGeom, swordMat);
    blade.position.set(-0.65 + i * 0.15, 0.95, -0.85);
    blade.rotation.z = 0.1;
    group.add(blade);

    const handleGeom = new THREE.BoxGeometry(0.025, 0.12, 0.015);
    const handle = new THREE.Mesh(handleGeom, darkWoodMat);
    handle.position.set(-0.65 + i * 0.15, 0.68, -0.85);
    handle.rotation.z = 0.1;
    group.add(handle);
  }

  // Training dummy
  const dummyBaseMat = new THREE.MeshStandardMaterial({ color: 0xd4a060, roughness: 0.9 });

  const dummyPostGeom = new THREE.CylinderGeometry(0.04, 0.05, 0.7, 6);
  const dummyPost = new THREE.Mesh(dummyPostGeom, woodMat);
  dummyPost.position.set(0.6, 0.78, -0.5);
  group.add(dummyPost);

  const dummyBodyGeom = new THREE.CylinderGeometry(0.1, 0.12, 0.35, 8);
  const dummyBody = new THREE.Mesh(dummyBodyGeom, dummyBaseMat);
  dummyBody.position.set(0.6, 1.1, -0.5);
  group.add(dummyBody);

  const dummyArmGeom = new THREE.BoxGeometry(0.4, 0.05, 0.05);
  const dummyArm = new THREE.Mesh(dummyArmGeom, dummyBaseMat);
  dummyArm.position.set(0.6, 1.15, -0.5);
  group.add(dummyArm);

  const dummyHeadGeom = new THREE.SphereGeometry(0.07, 8, 6);
  const dummyHead = new THREE.Mesh(dummyHeadGeom, dummyBaseMat);
  dummyHead.position.set(0.6, 1.35, -0.5);
  group.add(dummyHead);
}
